import type React from "react";

import { Trash2, GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { StatusBadge } from "~/molecules/WorkflowStatusBadge";
import { cn } from "~/lib/utils";
import { type WorkflowStepInput } from "~/api/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

type StepHeaderProps = {
  index: number;
  isEditMode: boolean;
  step: WorkflowStepInput;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  error?: string;
  hasStepInstance: boolean;
};

export function StepHeader({
  index,
  isEditMode,
  step,
  onTitleChange,
  onRemove,
  error,
  hasStepInstance,
}: StepHeaderProps) {
  const { t } = useTranslation();

  const isUpcoming = step.status === "upcoming";

  const getDeleteTooltip = () => {
    if (!isUpcoming) return t(($) => $.commons.workflow.cannotDeleteOngoing);
    if (hasStepInstance)
      return t(($) => $.commons.workflow.cannotDeleteWithInstance);
    if (index === 0) return t(($) => $.commons.workflow.cannotDeleteFirstStep);
    return t(($) => $.commons.workflow.delete);
  };

  return (
    <div className="border-border/30 bg-secondary/30 flex flex-col gap-2 border-b p-2">
      <div className="flex flex-row items-center gap-2">
        {isEditMode && (
          <div
            className={cn("rounded-full p-1", {
              "bg-primary/10 text-primary/70 cursor-move": isUpcoming,
              "bg-secondary/70 cursor-not-allowed": !isUpcoming,
            })}
          >
            <GripVertical className="h-5 w-5" />
          </div>
        )}

        {isEditMode && step.status !== "completed" ? (
          <Input
            value={step.name}
            onChange={onTitleChange}
            placeholder={`Step ${index + 1}`}
            className={cn({ "border-red-500 focus:border-red-500": error })}
          />
        ) : (
          <h3 className="font-medium">{step.name}</h3>
        )}

        <div className="ml-auto flex items-center gap-2">
          <StatusBadge status={step.status} />

          {isEditMode && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-red-50 text-red-700"
                      onClick={onRemove}
                      disabled={!isUpcoming || hasStepInstance || index === 0}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">
                        {t(($) => $.commons.workflow.delete)}
                      </span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{getDeleteTooltip()}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {error && <div className="pl-8 text-xs text-red-500">{error}</div>}
    </div>
  );
}
